import React, { memo, useCallback, useRef, useEffect, useMemo } from 'react';
import { VariableSizeList as List } from 'react-window';
import MessageBubble from './MessageBubble';
import { useDarkMode } from '../../../contexts/DarkModeContext';

// ⚡️ PERFORMANCE OPTIMIZATION: Virtualized message list for large chat histories
const VirtualizedMessageList = memo(({
  messages,
  user,
  isAdminDashboard,
  conversation,
  onImageClick,
  renderMessageStatus,
  containerHeight = 600,
  itemHeight = 80
}) => {
  const { isDarkMode } = useDarkMode();
  const listRef = useRef(null);
  const lastMessageRef = useRef(null);
  
  // 🚀 HEIGHT CACHING: Cache calculated heights to avoid recalculation
  const heightCache = useRef(new Map());

  // 🚀 ADAPTIVE VIRTUALIZATION: Dynamic overscan based on scroll velocity
  const velocityRef = useRef(0);
  const lastTs = useRef(performance.now());
  const lastY = useRef(0);

  // ✅ FIX: Remove aggressive auto-scroll behavior
  // Auto-scroll is now handled by the parent component's scroll management
  // This prevents conflicts and allows users to scroll up to read old messages
  // useEffect(() => {
  //   if (messages.length > 0 && listRef.current) {
  //     const lastIndex = messages.length - 1;
  //     listRef.current.scrollToItem(lastIndex, 'end');
  //   }
  // }, [messages.length]);

  // 🚀 STABLE ITEM KEY: Generate stable keys to prevent unnecessary re-renders
  const getItemKey = useCallback((index) => {
    const message = messages[index];
    if (!message) return `empty-${index}`;
    
    // Use message ID if available, otherwise create stable key from timestamp and index
    return message.id || `${message.timestamp || Date.now()}-${index}`;
  }, [messages]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Memoized row renderer
  const Row = useCallback(({ index, style }) => {
    const message = messages[index];
    if (!message) return null;

    return (
      <div style={style}>
        <MessageBubble
          msg={message}
          user={user}
          isAdminDashboard={isAdminDashboard}
          conversation={conversation}
          onImageClick={onImageClick}
          renderMessageStatus={renderMessageStatus}
        />
      </div>
    );
  }, [messages, user, isAdminDashboard, conversation, onImageClick, renderMessageStatus]);

  // 🚀 HEIGHT CACHING: Dynamic item height calculation with caching
  const getItemHeight = useCallback((index) => {
    const message = messages[index];
    if (!message) return itemHeight;

    // Generate cache key
    const cacheKey = getItemKey(index);
    
    // Check cache first
    if (heightCache.current.has(cacheKey)) {
      return heightCache.current.get(cacheKey);
    }

    let height = itemHeight;

    // Adjust height based on message type and content
    if (message.type === 'image' || message.type === 'video') {
      height = 200; // Media messages need more space
    } else if (message.text) {
      // Helper function to safely get text content
      const getTextContent = (text) => {
        if (typeof text === 'string') return text;
        if (text && typeof text === 'object' && text.sanitized) return text.sanitized;
        return '';
      };
      
      const textContent = getTextContent(message.text);
      if (textContent.length > 100) {
        height = Math.max(itemHeight, 60 + Math.floor(textContent.length / 50) * 20);
      }
    }

    // Cache the calculated height
    heightCache.current.set(cacheKey, height);
    
    // 🚀 CACHE MANAGEMENT: Limit cache size to prevent memory leaks
    if (heightCache.current.size > 1000) {
      // Remove oldest 20% of entries
      const keysToDelete = Array.from(heightCache.current.keys()).slice(0, 200);
      keysToDelete.forEach(key => heightCache.current.delete(key));
    }

    return height;
  }, [messages, itemHeight, getItemKey]);

  // 🚀 CACHE CLEANUP: Clear height cache when messages array changes significantly
  useEffect(() => {
    const cleanupCache = () => {
      if (heightCache.current.size > 500) {
        heightCache.current.clear();
      }
    };

    // Clean cache when messages array length changes significantly
    const currentLength = messages.length;
    if (currentLength < heightCache.current.size * 0.5) {
      cleanupCache();
    }
  }, [messages.length]);

  // 🚀 ADAPTIVE SCROLL HANDLER: Track scroll velocity for dynamic overscan
  const handleScroll = useCallback((e) => {
    const t = performance.now();
    const dy = Math.abs(e.scrollOffset - lastY.current);
    velocityRef.current = dy / (t - lastTs.current + 1);
    lastTs.current = t;
    lastY.current = e.scrollOffset;
  }, []);

  // 🚀 ADAPTIVE OVERSCAN: Dynamic overscan based on scroll velocity
  const adaptiveOverscan = useMemo(() => {
    return velocityRef.current > 2 ? 12 : 6; // More overscan during fast scrolling
  }, [velocityRef.current]);

  // 🚀 MEMOIZED: Calculate total height for better performance
  const totalHeight = useMemo(() => {
    if (messages.length <= 50) return 'auto';
    return containerHeight;
  }, [messages.length, containerHeight]);

  if (!messages || messages.length === 0) {
    return (
      <div className={`flex items-center justify-center h-full ${
        isDarkMode ? 'text-gray-400' : 'text-gray-500'
      }`}>
        No messages yet
      </div>
    );
  }

  // ⚡️ FALLBACK: Use regular rendering for smaller lists to avoid virtualization issues
  if (messages.length <= 50) {
    // Filter out any null, undefined, or empty messages to prevent space box issues
    const validMessages = messages.filter(msg => {
      if (!msg) return false;
      
      // Helper function to safely check if text exists
      const hasTextContent = (text) => {
        if (typeof text === 'string') return text.trim() !== '';
        if (text && typeof text === 'object' && text.sanitized) return text.sanitized.trim() !== '';
        return false;
      };
      
      return hasTextContent(msg.text) || msg.type === 'image' || msg.type === 'video' || msg.type === 'order';
    });
    
    return (
      <div className="virtualized-message-list custom-scrollbar" style={{ height: containerHeight, overflowY: 'auto', paddingBottom: 0 }}>
        {validMessages.map((message, index) => (
          <MessageBubble
            key={getItemKey(index)}
            msg={message}
            user={user}
            isAdminDashboard={isAdminDashboard}
            conversation={conversation}
            onImageClick={onImageClick}
            renderMessageStatus={renderMessageStatus}
          />
        ))}
      </div>
    );
  }

  // ⚡️ VIRTUALIZATION: Use VariableSizeList for large message lists with enhanced settings
  return (
    <div className="virtualized-message-list">
      <List
        ref={listRef}
        height={containerHeight}
        itemCount={messages.length}
        itemSize={getItemHeight}
        width="100%"
        overscanCount={adaptiveOverscan} // 🚀 ADAPTIVE OVERSCAN: Dynamic based on scroll velocity
        itemKey={getItemKey} // 🚀 STABLE KEYS: Prevent unnecessary re-renders
        className="custom-scrollbar"
        onScroll={handleScroll} // 🚀 VELOCITY TRACKING: Monitor scroll speed
        // 🚀 ENHANCED SETTINGS: Better performance and UX
        estimatedItemSize={itemHeight}
        onItemsRendered={({ visibleStartIndex, visibleStopIndex }) => {
        }}
      >
        {Row}
      </List>
    </div>
  );
});

VirtualizedMessageList.displayName = 'VirtualizedMessageList';

export default VirtualizedMessageList; 